// app/src/lib/lock/deviceLock.js — device lock: a master key (MK) wrapped two
// ways (WebAuthn PRF biometric + recovery passphrase). See docs/LOCAL_LOCK_DESIGN.md.
//
// SPIKE SCOPE: this builds + validates the unlock lifecycle and UX. It produces a
// REAL master key (exposed via getMasterKey() for the next step) but does NOT yet
// encrypt the Dexie store or the refresh token. KDF is PBKDF2 as a placeholder for
// Argon2id (see design doc) — the choice doesn't affect UX.
//
// Threat model: an attacker with read access to the device's IndexedDB/profile.
// MK never touches persistent storage in the clear — only the two AES-GCM-wrapped
// envelopes are stored. Without the biometric authenticator OR the passphrase,
// the envelopes are undecryptable.

import * as kv from '../idbKv.js';
import { b64uEncode, b64uDecode } from '../crypto.js';

const LOCK_KEY = 'kanjo.lock.v1';
const PBKDF2_ITERATIONS = 600_000; // TODO: migrate to Argon2id (WASM) per design doc.
const enc = new TextEncoder();

/** In-memory master key (AES-GCM CryptoKey) while unlocked; null when locked. */
let masterKey = null;

const rand = (n) => crypto.getRandomValues(new Uint8Array(n));

// Recovery-code alphabet: 29 unambiguous chars (no I/L/O/U/0/1). A 16-char code
// (~78 bits) is the passphrase for the recovery wrap — generated FOR the user so
// enrollment needs no typing. Daily unlock is biometric; this is only entered on
// a new device.
const RC_ALPHABET = 'ABCDEFGHJKMNPQRSTVWXYZ23456789';

/** Generate a grouped recovery code, e.g. "K9F2-7QXM-4ABT-9WZP". */
export function generateRecoveryCode() {
  const bytes = rand(16);
  let s = '';
  for (let i = 0; i < 16; i++) s += RC_ALPHABET[bytes[i] % RC_ALPHABET.length];
  return s.match(/.{1,4}/g).join('-');
}

/** Canonicalize sloppy recovery-code entry (lowercase / missing dashes) back to
 *  the stored form, or null if it isn't a 16-char recovery code. */
function canonRecovery(s) {
  const up = String(s || '').toUpperCase().replace(new RegExp(`[^${RC_ALPHABET}]`, 'g'), '');
  return up.length === 16 ? up.match(/.{1,4}/g).join('-') : null;
}
/** RP ID = the registrable origin host. localhost in dev, app.* in prod. */
const rpId = () => window.location.hostname;
const aadFor = (sub) => enc.encode(`kanjo/lock/v1|${sub}`);
const zero = (u8) => { if (u8 && typeof u8.fill === 'function') u8.fill(0); };

async function claims() {
  const { currentClaims } = await import('../auth/cognitoClient.js');
  return currentClaims();
}
async function requireSub() {
  const c = await claims();
  if (!c || !c.sub) throw new Error('lock: not signed in');
  return c.sub;
}

const readConfig = async () => (await kv.get(LOCK_KEY)) || null;
const writeConfig = (cfg) => kv.set(LOCK_KEY, cfg);

// ── KEK derivations ─────────────────────────────────────────────────────────

async function passphraseKek(passphrase, saltBytes) {
  const base = await crypto.subtle.importKey('raw', enc.encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: saltBytes, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

async function prfKek(prfOutput, hkdfSalt) {
  const ikm = await crypto.subtle.importKey('raw', prfOutput, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: hkdfSalt, info: enc.encode('kanjo/lock/prf/v1') },
    ikm,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

// ── wrap / unwrap the master key ─────────────────────────────────────────────

async function wrapMk(kek, mkRaw, aad) {
  const iv = rand(12);
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: aad }, kek, mkRaw));
  return { iv: b64uEncode(iv), ct: b64uEncode(ct) };
}
async function unwrapMkRaw(kek, wrap, aad) {
  const iv = b64uDecode(wrap.iv);
  const ct = b64uDecode(wrap.ct);
  return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv, additionalData: aad }, kek, ct));
}
/** Import raw MK bytes as a NON-extractable AES-GCM key, then zero the caller's bytes. */
async function adoptMk(mkRaw) {
  masterKey = await crypto.subtle.importKey('raw', mkRaw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
  zero(mkRaw);
}

// ── WebAuthn PRF ─────────────────────────────────────────────────────────────

/** Is the WebAuthn API present at all? (PRF support is only knowable after a ceremony.) */
export function isWebauthnAvailable() {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential && !!(navigator.credentials && navigator.credentials.create);
}

/** Run a get() assertion and return the PRF output bytes, or null if PRF unsupported. */
async function evalPrf(credentialId, prfSalt) {
  const assertion = await navigator.credentials.get({
    publicKey: {
      rpId: rpId(),
      challenge: rand(32),
      allowCredentials: [{ type: 'public-key', id: credentialId }],
      userVerification: 'required',
      timeout: 60_000,
      extensions: { prf: { eval: { first: prfSalt } } },
    },
  });
  const res = assertion && assertion.getClientExtensionResults && assertion.getClientExtensionResults().prf;
  if (!res || !res.results || !res.results.first) return null;
  return new Uint8Array(res.results.first);
}

/** Create a platform passkey and obtain its PRF secret. Throws code=PRF_UNSUPPORTED if none. */
async function createPrfCredential(sub, email) {
  const prfSalt = rand(32);
  const cred = await navigator.credentials.create({
    publicKey: {
      rp: { id: rpId(), name: 'Kanjo' },
      user: { id: enc.encode(sub), name: email || sub, displayName: email || 'Kanjo' },
      challenge: rand(32),
      pubKeyCredParams: [{ type: 'public-key', alg: -7 }, { type: 'public-key', alg: -257 }],
      authenticatorSelection: { authenticatorAttachment: 'platform', userVerification: 'required', residentKey: 'preferred' },
      timeout: 60_000,
      attestation: 'none',
      extensions: { prf: {} },
    },
  });
  if (!cred) throw new Error('lock: credential creation returned null');
  const credentialId = new Uint8Array(cred.rawId);
  // Most platforms (notably iOS) only return the PRF output on a subsequent get().
  const prfOutput = await evalPrf(credentialId, prfSalt);
  if (!prfOutput) {
    const e = new Error('lock: authenticator has no PRF support');
    e.code = 'PRF_UNSUPPORTED';
    throw e;
  }
  return { credentialId, prfSalt, prfOutput };
}

// ── public state ─────────────────────────────────────────────────────────────

export async function isEnrolled() {
  const c = await readConfig();
  return !!(c && c.pass);
}
export async function hasBiometric() {
  const c = await readConfig();
  return !!(c && c.prf);
}
export function isUnlocked() {
  return masterKey !== null;
}
/** The in-memory master key, or null when locked. For the data-at-rest wiring step. */
export function getMasterKey() {
  return masterKey;
}
export function lock() {
  masterKey = null;
}

// ── enrollment / unlock ──────────────────────────────────────────────────────

/**
 * First-time setup. Always sets a passphrase wrap; optionally adds a biometric wrap.
 * Leaves the device unlocked. Returns { biometricEnabled, prfWarning }.
 * @param {{ passphrase: string, useBiometric?: boolean }} opts
 */
export async function enroll({ passphrase, useBiometric = true }) {
  if (typeof passphrase !== 'string' || passphrase.length < 8) {
    throw new Error('lock: passphrase must be at least 8 characters');
  }
  if (await isEnrolled()) throw new Error('lock: already enrolled');
  const sub = await requireSub();
  const aad = aadFor(sub);
  const mkRaw = rand(32);

  // Passphrase wrap (always).
  const passSalt = rand(16);
  const passWrap = await wrapMk(await passphraseKek(passphrase, passSalt), mkRaw, aad);
  const cfg = {
    v: 1,
    createdAt: Date.now(),
    pass: { kdf: 'PBKDF2-SHA256', iterations: PBKDF2_ITERATIONS, salt: b64uEncode(passSalt), wrap: passWrap },
    prf: null,
  };

  // Biometric wrap (optional, best-effort).
  let biometricEnabled = false;
  let prfWarning = null;
  if (useBiometric && isWebauthnAvailable()) {
    try {
      const c = await claims();
      const { credentialId, prfSalt, prfOutput } = await createPrfCredential(sub, c && c.email);
      const hkdfSalt = rand(32);
      const prfWrap = await wrapMk(await prfKek(prfOutput, hkdfSalt), mkRaw, aad);
      zero(prfOutput);
      cfg.prf = {
        credentialId: b64uEncode(credentialId),
        prfSalt: b64uEncode(prfSalt),
        hkdfSalt: b64uEncode(hkdfSalt),
        wrap: prfWrap,
      };
      biometricEnabled = true;
    } catch (e) {
      prfWarning = describePrfError(e);
    }
  } else if (useBiometric) {
    prfWarning = 'Biometric unlock is not available on this device; your passphrase will be used.';
  }

  await writeConfig(cfg);
  await adoptMk(mkRaw); // zeroes mkRaw
  return { biometricEnabled, prfWarning };
}

/** Unlock by re-deriving from the passphrase. Returns true on success, false on wrong passphrase. */
export async function unlockWithPassphrase(passphrase) {
  const c = await readConfig();
  if (!c || !c.pass) throw new Error('lock: not enrolled');
  const aad = aadFor(await requireSub());
  const salt = b64uDecode(c.pass.salt);
  // Try the input as typed, then (for recovery codes) a canonicalized form so a
  // code entered lowercase / without dashes on a new phone still matches.
  const attempts = [passphrase];
  const canon = canonRecovery(passphrase);
  if (canon && canon !== passphrase) attempts.push(canon);
  for (const attempt of attempts) {
    try {
      const kek = await passphraseKek(attempt, salt);
      const mkRaw = await unwrapMkRaw(kek, c.pass.wrap, aad);
      await adoptMk(mkRaw);
      return true;
    } catch { /* GCM tag mismatch ⇒ wrong secret; try next form */ }
  }
  return false;
}

/** Unlock via biometric (WebAuthn PRF). Throws on unsupported/cancelled; returns false if the wrap fails. */
export async function unlockWithBiometric() {
  const c = await readConfig();
  if (!c || !c.prf) { const e = new Error('lock: no biometric enrolled'); e.code = 'NO_BIOMETRIC'; throw e; }
  if (!isWebauthnAvailable()) { const e = new Error('lock: WebAuthn unavailable'); e.code = 'NO_WEBAUTHN'; throw e; }
  const aad = aadFor(await requireSub());
  const prfOutput = await evalPrf(b64uDecode(c.prf.credentialId), b64uDecode(c.prf.prfSalt));
  if (!prfOutput) { const e = new Error('lock: PRF unavailable'); e.code = 'PRF_UNSUPPORTED'; throw e; }
  const kek = await prfKek(prfOutput, b64uDecode(c.prf.hkdfSalt));
  zero(prfOutput);
  let mkRaw;
  try { mkRaw = await unwrapMkRaw(kek, c.prf.wrap, aad); }
  catch { return false; }
  await adoptMk(mkRaw);
  return true;
}

/**
 * Add a biometric wrap to an already-enrolled device. Requires the passphrase
 * (to recover MK) rather than the in-memory key, which is non-extractable.
 * Returns { biometricEnabled, prfWarning }.
 */
export async function addBiometric(passphrase) {
  const c = await readConfig();
  if (!c || !c.pass) throw new Error('lock: not enrolled');
  if (c.prf) return { biometricEnabled: true, prfWarning: null };
  if (!isWebauthnAvailable()) return { biometricEnabled: false, prfWarning: 'Biometric unlock is not available on this device.' };
  const sub = await requireSub();
  const aad = aadFor(sub);
  const kek = await passphraseKek(passphrase, b64uDecode(c.pass.salt));
  let mkRaw;
  try { mkRaw = await unwrapMkRaw(kek, c.pass.wrap, aad); }
  catch { throw new Error('lock: passphrase incorrect'); }
  try {
    const cl = await claims();
    const { credentialId, prfSalt, prfOutput } = await createPrfCredential(sub, cl && cl.email);
    const hkdfSalt = rand(32);
    const prfWrap = await wrapMk(await prfKek(prfOutput, hkdfSalt), mkRaw, aad);
    zero(prfOutput);
    c.prf = {
      credentialId: b64uEncode(credentialId),
      prfSalt: b64uEncode(prfSalt),
      hkdfSalt: b64uEncode(hkdfSalt),
      wrap: prfWrap,
    };
    await writeConfig(c);
    return { biometricEnabled: true, prfWarning: null };
  } catch (e) {
    return { biometricEnabled: false, prfWarning: describePrfError(e) };
  } finally {
    zero(mkRaw);
  }
}

/**
 * Replace the recovery secret on this device, keeping the master key.
 *
 * The code is generated once at enrollment and shown once, so the owner who
 * closed that screen too fast — or read the code out on a call — has no way
 * back short of turning the lock off and on again. This re-wraps the SAME
 * master key under a fresh secret and a fresh KDF salt: every byte already
 * encrypted stays readable, the biometric envelope is untouched, and the old
 * code stops working the moment the new config is written.
 *
 * Proof of current access is required, because rotation would otherwise let
 * anyone holding an unlocked phone mint themselves a key to it. That proof is
 * the current passphrase, or `viaBiometric` for a fresh WebAuthn ceremony.
 *
 * @param {{ current?: string, viaBiometric?: boolean, next?: string }} opts
 *   `current` the passphrase/recovery code in force (accepted sloppily, as on
 *   the unlock screen); `viaBiometric` proves access with Face ID instead;
 *   `next` sets a passphrase of the owner's choosing, otherwise one is
 *   generated for them.
 * @returns {Promise<{ code: string, generated: boolean }>} the new secret — the
 *   only time it is ever available in the clear.
 */
export async function rotateRecovery({ current = null, viaBiometric = false, next = null } = {}) {
  const cfg = await readConfig();
  if (!cfg || !cfg.pass) throw new Error('lock: not enrolled');
  if (next !== null && (typeof next !== 'string' || next.length < 8)) {
    throw new Error('lock: passphrase must be at least 8 characters');
  }
  const aad = aadFor(await requireSub());

  // Recover the master key from whichever envelope the caller can open.
  let mkRaw = null;
  if (viaBiometric) {
    if (!cfg.prf) { const e = new Error('lock: no biometric enrolled'); e.code = 'NO_BIOMETRIC'; throw e; }
    const prfOutput = await evalPrf(b64uDecode(cfg.prf.credentialId), b64uDecode(cfg.prf.prfSalt));
    if (!prfOutput) { const e = new Error('lock: PRF unavailable'); e.code = 'PRF_UNSUPPORTED'; throw e; }
    const kek = await prfKek(prfOutput, b64uDecode(cfg.prf.hkdfSalt));
    zero(prfOutput);
    try { mkRaw = await unwrapMkRaw(kek, cfg.prf.wrap, aad); }
    catch { throw new Error('lock: biometric unlock failed'); }
  } else {
    const salt = b64uDecode(cfg.pass.salt);
    // Same sloppy-entry tolerance as unlockWithPassphrase: someone retyping the
    // code they saved should not be told it is wrong over a missing dash.
    const attempts = [String(current || '')];
    const canon = canonRecovery(current);
    if (canon && canon !== current) attempts.push(canon);
    for (const attempt of attempts) {
      try {
        mkRaw = await unwrapMkRaw(await passphraseKek(attempt, salt), cfg.pass.wrap, aad);
        break;
      } catch { /* GCM tag mismatch ⇒ wrong secret; try next form */ }
    }
    if (!mkRaw) throw new Error('lock: passphrase incorrect');
  }

  const code = next === null ? generateRecoveryCode() : next;
  const passSalt = rand(16);
  const wrap = await wrapMk(await passphraseKek(code, passSalt), mkRaw, aad);
  // Write before adopting: if the write throws, the OLD config is still the one
  // on disk and the old code still opens it.
  await writeConfig({
    ...cfg,
    pass: { kdf: 'PBKDF2-SHA256', iterations: PBKDF2_ITERATIONS, salt: b64uEncode(passSalt), wrap },
    rotatedAt: Date.now(),
  });
  await adoptMk(mkRaw); // zeroes mkRaw; leaves the device unlocked
  return { code, generated: next === null };
}

/** Remove the lock entirely (both wraps) and lock memory. Data re-keying TBD. */
export async function disable() {
  await kv.del(LOCK_KEY);
  lock();
}

function describePrfError(e) {
  if (e && e.code === 'PRF_UNSUPPORTED') return 'This device does not support biometric unlock; your passphrase will be used.';
  if (e && (e.name === 'NotAllowedError' || e.name === 'AbortError')) return 'Biometric setup was cancelled.';
  if (e && e.name === 'SecurityError') return 'Biometric setup needs a secure (https) origin.';
  return 'Biometric setup failed; your passphrase will be used.';
}
